import type * as TogglePrimitive from '@radix-ui/react-toggle';
import React, { type ComponentProps, useState } from 'react';
import type { ButtonProps } from '../radix/button';
import { Button } from '../radix/button';
import { Toggle } from '../radix/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '../radix/tooltip';

type TooltipButtonProps = {
  toolTipContent: string | React.ReactNode;
  toolTipEnabled?: boolean;
  toolTipSide?: ComponentProps<typeof TooltipContent>['side'];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} & ButtonProps;

export const TooltipButton = ({
  ref,
  toolTipContent,
  toolTipEnabled = true,
  open,
  onOpenChange,
  toolTipSide,
  ...props
}: TooltipButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(open ?? false);

  if (!toolTipEnabled) return <Button {...props} ref={ref} />;
  return (
    <Tooltip
      onOpenChange={(val) => {
        setTooltipOpen(val);
        onOpenChange?.(val);
      }}
      open={open ?? tooltipOpen}
    >
      <TooltipTrigger asChild>
        <Button {...props} ref={ref} />
      </TooltipTrigger>
      <TooltipContent
        {...(toolTipSide === undefined ? {} : { side: toolTipSide })}
      >
        {toolTipContent}
      </TooltipContent>
    </Tooltip>
  );
};
TooltipButton.displayName = 'TooltipButton';

const TooltipToggle = ({
  toolTipContent,
  ...props
}: TogglePrimitive.ToggleProps & {
  toolTipContent: string | React.ReactNode;
} & {
  ref?: React.RefObject<React.ComponentRef<typeof TogglePrimitive.Root> | null>;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span>
        {/* Breaks accessibility https://github.com/radix-ui/primitives/discussions/560#discussioncomment-3477536 */}
        <Toggle {...props} />
      </span>
    </TooltipTrigger>
    <TooltipContent>{toolTipContent}</TooltipContent>
  </Tooltip>
);
TooltipToggle.displayName = 'TooltipToggle';
